import React, { useState, useCallback } from 'react';
import useAcademicHealth from './useAcademicHealth';
import { useDataOptional } from '../../contexts/DataContext';
import ProfileImageSelector from './ProfileImageSelector';
import TileDetailModal from './TileDetailModal';
import Next7DaysModal from './Next7DaysModal';
import DashboardThemeSwitcher from './DashboardThemeSwitcher';
import './DashboardHeader.css';

/**
 * DashboardHeader — responsive "Command Header".
 *
 * Row 1 (rowPrimary):  identity block | Academic Health block
 * Row 2 (rowSecondary): KPI tiles (Overdue, Next 7 Days progress, Next Up)
 *
 * Drawer state is lifted to the parent (CenterColumn / HomeView);
 * this component communicates via onDrawerToggle / drawerOpen props.
 */

/* ── Tile icon SVGs (16px feather-style) ── */
const TileIcon = ({ type }) => {
  const p = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (type === 'overdue') return (
    <svg {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
  );
  if (type === 'next7') return (
    <svg {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/></svg>
  );
  // clock
  return (
    <svg {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
  );
};

/* ── Overdue Tile ── */
function OverdueTile({ count, onClick, hasCourses }) {
  const tone = count > 0 ? 'danger' : 'good';
  return (
    <div
      className={`dh-tile dh-tile--${tone}${onClick ? ' dh-tile--clickable' : ''}${!hasCourses ? ' dh-tile--dormant' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <span className="dh-tile__label">
        <TileIcon type="overdue" />
        Overdue
      </span>
      <span className="dh-tile__primary">
        {count} <span className="dh-tile__unit">assignment{count !== 1 ? 's' : ''}</span>
      </span>
      <span className="dh-tile__context">
        {count === 0 ? "You're all caught up" : 'Needs attention'}
      </span>
    </div>
  );
}

/* ── Next 7 Days Tile ── */
function Next7DaysTile({ health, onClick, hasCourses }) {
  const total = health.next7dTotal;
  const done = health.next7dCompleted;
  const remaining = health.next7dRemaining;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  let barTone;
  if (pct >= 80) barTone = 'green';
  else if (pct >= 40) barTone = 'yellow';
  else barTone = 'red';

  if (!hasCourses) {
    return (
      <div className="dh-tile dh-tile--neutral dh-tile--dormant">
        <span className="dh-tile__label">
          <TileIcon type="next7" />
          Next 7 Days
        </span>
        <span className="dh-tile__primary dh-tile__primary--muted">No assignments yet</span>
        <span className="dh-tile__context">Add a course to begin tracking</span>
      </div>
    );
  }

  return (
    <div
      className="dh-tile dh-tile--neutral dh-tile--clickable"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <span className="dh-tile__label">
        <TileIcon type="next7" />
        Next 7 Days
      </span>
      <span className="dh-tile__primary">
        {done} / {total} <span className="dh-tile__unit">completed</span>
      </span>
      <div className="dh-tile__bar">
        <div className={`dh-tile__barFill dh-tile__barFill--${barTone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="dh-tile__context">{remaining} remaining</span>
    </div>
  );
}

/* ── Next Up Tile ── */
function NextUpTile({ nextEvent, onClick, hasCourses }) {
  if (!nextEvent) {
    return (
      <div className={`dh-tile dh-tile--accent${!hasCourses ? ' dh-tile--dormant' : ''}`}>
        <span className="dh-tile__label">
          <TileIcon type="nextup" />
          Next Up
        </span>
        <span className="dh-tile__primary dh-tile__primary--muted">Nothing scheduled yet</span>
        <span className="dh-tile__context">Your next item will show here</span>
      </div>
    );
  }
  return (
    <div
      className="dh-tile dh-tile--accent dh-tile--clickable"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    >
      <span className="dh-tile__label">
        <TileIcon type="nextup" />
        Next Up
      </span>
      <span className="dh-tile__primary" title={nextEvent.title}>{nextEvent.title}</span>
      {nextEvent.courseName && (
        <span className="dh-tile__course">{nextEvent.courseName}</span>
      )}
      {nextEvent.date && (
        <span className="dh-tile__context">{formatShortTime(nextEvent.date)}</span>
      )}
    </div>
  );
}

/* ── Status icon SVGs (inline, no deps) ── */
const StatusIcon = ({ status, size = 16 }) => {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (status === 'critical') {
    // alert-octagon
    return (
      <svg {...props}>
        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    );
  }
  if (status === 'warning') {
    // alert-triangle
    return (
      <svg {...props}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  // good → check-circle
  return (
    <svg {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
};

/* ── Status config map ── */
const STATUS_CONFIG = {
  critical: { label: 'Critical',  chipClass: 'ahb__chip--critical' },
  warning:  { label: 'Warning',   chipClass: 'ahb__chip--warning'  },
  good:     { label: 'On Track',  chipClass: 'ahb__chip--good'     },
};

/* ── Academic Health Badge — minimal status badge (no numbers) ── */
function AcademicHealthBadge({ health, onClick, drawerOpen, hasCourses }) {
  const cfg = STATUS_CONFIG[health.status] || STATUS_CONFIG.good;

  const chipLabel = hasCourses ? cfg.label : 'Ready';
  const chipClass = hasCourses ? cfg.chipClass : 'ahb__chip--dormant';
  const helperText = hasCourses ? 'Learn more' : 'Add courses to activate insights';

  return (
    <div
      className={`ahb${hasCourses ? ` ahb--${health.status}` : ' ahb--dormant'}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      aria-expanded={drawerOpen}
      aria-label={`Academic Health: ${chipLabel}. Click to ${drawerOpen ? 'close' : 'open'} details.`}
      title="Shows your academic status and what to do next."
    >
      {/* Full layout (≥ 640px) — stacked square card */}
      <div className="ahb__full">
        <span className="ahb__label">Academic Health</span>
        <span className={`ahb__icon${hasCourses ? ` ahb__icon--${health.status}` : ' ahb__icon--dormant'}`}>
          <StatusIcon status={hasCourses ? health.status : 'good'} size={28} />
        </span>
        <span className={`ahb__chip ${chipClass}`}>{chipLabel}</span>
        <span className="ahb__link">{helperText}</span>
      </div>

      {/* Collapsed (< 640px) — compact horizontal pill */}
      <span className="ahb__mobileContent">
        <StatusIcon status={hasCourses ? health.status : 'good'} size={14} />
        <span className="ahb__mobileText">{chipLabel}</span>
      </span>
    </div>
  );
}

/* ── Main component ── */
export default function DashboardHeader({ userName, drawerOpen, onDrawerToggle }) {
  const health = useAcademicHealth();

  // ── Profile image selector state ──
  const [isProfileSelectorOpen, setIsProfileSelectorOpen] = useState(false);

  // ── Tile detail modal state ──
  const [tileModal, setTileModal] = useState(null); // 'overdue' | null
  const [next7dOpen, setNext7dOpen] = useState(false);
  const [selectedProfileImage, setSelectedProfileImage] = useState(() => {
    try {
      return localStorage.getItem('scoutiq-profile-image') || '/profile_pics/cap_gown_scout.png';
    } catch { return '/profile_pics/cap_gown_scout.png'; }
  });
  const [selectedFrameId, setSelectedFrameId] = useState(() => {
    try {
      return localStorage.getItem('scoutiq-profile-frame') || 'minimal-hairline';
    } catch { return 'minimal-hairline'; }
  });

  const handleProfileSave = useCallback((image, frameId) => {
    setSelectedProfileImage(image);
    setSelectedFrameId(frameId);
    setIsProfileSelectorOpen(false);
    try {
      localStorage.setItem('scoutiq-profile-image', image);
      localStorage.setItem('scoutiq-profile-frame', frameId);
    } catch (e) { console.warn('Failed to save profile preferences:', e); }
  }, []);

  const getFrameClass = (fid) => {
    const map = {
      'classic-black': 'frame-classic-black', 'silver-minimal': 'frame-silver-minimal',
      'gold-premium': 'frame-gold-premium', 'docuhounds-blue': 'frame-docuhounds-blue',
      'neon-blue': 'frame-neon-blue', 'neon-purple': 'frame-neon-purple',
      'pixel-white': 'frame-pixel-white', 'rounded-pill': 'frame-rounded-pill',
      'shadow-soft': 'frame-shadow-soft', 'double-line': 'frame-double-line',
      'grad-blue-purple': 'frame-grad-blue-purple', 'grad-orange-pink': 'frame-grad-orange-pink',
      'glass-morph': 'frame-glass-morph', 'cardboard': 'frame-cardboard',
      'cooler-lid': 'frame-cooler-lid', 'wireframe': 'frame-wireframe',
      'stadium-lights': 'frame-stadium-lights', 'study-hall': 'frame-study-hall',
      'dark-mode': 'frame-dark-mode', 'minimal-hairline': 'frame-minimal-hairline',
    };
    return map[fid] || 'frame-minimal-hairline';
  };

  const now = new Date();
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' });
  const monthDay = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  // Determine whether user has any courses (for smarter empty-state copy)
  const dataCtx = useDataOptional() || {};
  const hasCourses = Object.keys(dataCtx.state?.coursesById || {}).length > 0;

  return (
    <header className="dashHeader">
      {/* ── Row 1: identity + controls ── */}
      <div className="rowPrimary">
        <div className="identityBlock">
          <button
            className="avatarBtn"
            onClick={() => setIsProfileSelectorOpen(true)}
            aria-label="Change profile picture"
          >
            <div className={`scoutiq-hero-avatar-frame ${getFrameClass(selectedFrameId)}`}>
              <img src={selectedProfileImage} alt="Profile avatar" className="scoutiq-hero-avatar-img" />
            </div>
          </button>
          <div className="identityText">
            <h1 className="identityName">Welcome back, {userName}</h1>
            <DashboardThemeSwitcher />
            <div className="subline">{weekday} &bull; {monthDay}</div>
          </div>
        </div>

        <div className="rowPrimary__right">
          <AcademicHealthBadge
            health={health}
            onClick={onDrawerToggle}
            drawerOpen={drawerOpen}
            hasCourses={hasCourses}
          />
        </div>
      </div>

      {/* ── Row 2: KPI tiles ── */}
      <div className="rowSecondary">
        <div className="tiles">
          <OverdueTile count={health.overdueCount} onClick={() => setTileModal('overdue')} hasCourses={hasCourses} />
          <Next7DaysTile health={health} onClick={() => setNext7dOpen(true)} hasCourses={hasCourses} />
          <NextUpTile
            nextEvent={health.nextEvent}
            hasCourses={hasCourses}
            onClick={() => {
              const ev = health.nextEvent;
              if (ev) {
                window.dispatchEvent(new CustomEvent('scoutiq:openEventModal', {
                  detail: { event: ev }
                }));
              }
            }}
          />
        </div>
      </div>

      {/* Profile Image Selector Modal */}
      <ProfileImageSelector
        isOpen={isProfileSelectorOpen}
        onClose={() => setIsProfileSelectorOpen(false)}
        selectedImage={selectedProfileImage}
        selectedFrameId={selectedFrameId}
        onSave={handleProfileSave}
      />

      {/* Tile Detail Modal (Overdue) */}
      <TileDetailModal
        isOpen={tileModal === 'overdue'}
        mode="overdue"
        items={health.overdueItems}
        onClose={() => setTileModal(null)}
      />

      {/* Next 7 Days Progress Modal */}
      <Next7DaysModal
        isOpen={next7dOpen}
        onClose={() => setNext7dOpen(false)}
        incompleteItems={health.dueNext7dItems}
        completedItems={health.next7dCompletedItems}
      />
    </header>
  );
}

/* ── Helpers ── */

function formatShortTime(dateStr) {
  try {
    const d = new Date(dateStr);
    const wday = d.toLocaleDateString('en-US', { weekday: 'short' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${wday} ${time}`;
  } catch { return ''; }
}
